// processMotorTools — I-005: dispatch-layer som kopplar AI-tool-namn
// (från aiToolWrappersExtended) till deras impl-funktioner för
// process-motorn (P-001 + P-002 infrastruktur).
//
// AI-dispatcher (t.ex. aiAgents/llmAgentRunner) använder denna modul
// för att mappa \`tool_use\`-block i LLM-response → faktisk call.
// Enterprise-tier-gate är server-side (workflow_runs-insert-trigger)
// + client-side (minTier:'enterprise' i tool-definitionen).
//
// Approval-flow: AI ska INTE auto-trigga workflow-runs. Caller
// (AiAssistantDialog) MÅSTE visa en approval-dialog innan denna tool-
// dispatch körs. Vi sätter \`requiresApproval: true\` på writes så
// dispatcher kan enforca det.

import { triggerWorkflowRun } from '@/lib/runTrigger';
import { listMyRecentRuns, type RunRow } from '@/lib/runHistory';
import {
  PROCESS_MOTOR_TOOL_NAMES,
  isProcessMotorTool,
  requiresApproval,
  type ProcessMotorToolName,
} from '@/lib/processMotorToolConstants';

// Re-exportera pure constants så existerande callers inte behöver ändra import.
export {
  PROCESS_MOTOR_TOOL_NAMES,
  isProcessMotorTool,
  requiresApproval,
};
export type { ProcessMotorToolName };

export interface ToolCall {
  name: ProcessMotorToolName;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  args: Record<string, any>;
}

export interface ToolResult {
  ok: boolean;
  output?: unknown;
  error?: string;
  /** True om tool:en skriver state (runs-insert) — caller ska ha
   *  bekräftat approval innan call. */
  requiresApproval?: boolean;
}

/** Dispatch för process-motor-tools. Kastar inte — returnerar ToolResult
 *  med ok:false vid error. */
export async function dispatchProcessMotorTool(
  call: ToolCall,
  options: { approved?: boolean } = {},
): Promise<ToolResult> {
  // Write-tool kräver explicit approval flag
  if (requiresApproval(call.name) && !options.approved) {
    return {
      ok: false,
      error: 'approval-required',
      requiresApproval: true,
    };
  }

  switch (call.name) {
    case 'trigger_workflow_run': {
      const workflowId = String(call.args.workflow_id ?? '');
      if (!workflowId) {
        return { ok: false, error: 'missing-workflow_id' };
      }
      const triggerKind = call.args.trigger_kind === 'test' ? 'test' : 'manual';
      const result = await triggerWorkflowRun({
        workflowId,
        triggerData: call.args.trigger_data,
        triggerKind,
      });
      if (!result.success) {
        return { ok: false, error: result.error ?? 'trigger-failed' };
      }
      return {
        ok: true,
        output: { run_id: result.runId, status: result.status },
        requiresApproval: true,
      };
    }
    case 'list_recent_workflow_runs': {
      const workflowId = call.args.workflow_id
        ? String(call.args.workflow_id)
        : undefined;
      const limit = typeof call.args.limit === 'number' ? call.args.limit : 50;
      const result = await listMyRecentRuns(workflowId, limit);
      if (!result.success) {
        return { ok: false, error: result.error ?? 'list-failed' };
      }
      return {
        ok: true,
        output: { runs: result.runs as RunRow[] },
      };
    }
    default: {
      // Unreachable given type-guard above but TS exhaustiveness check
      const _exhaustive: never = call.name;
      return { ok: false, error: `unknown-tool:${String(_exhaustive)}` };
    }
  }
}
